import type { Ll87Message } from "@/pages/Filings/LL87Submission/Flow/adapterTypes";

import api from "./base";

// Define the TypeScript interfaces for the compliance data
export interface LL97EmissionSource {
  resource: string;
  usage: number;
  tCO2e: number;
}

export interface LL97PropertyLimit {
  type: string;
  gfa: number;
  limit: number;
  percentage: number;
}

export interface LL97PeriodData {
  period: string;
  emissions2024Calculated: number;
  totalLimit: number;
  isCompliant: boolean;
  propertyLimits: LL97PropertyLimit[];
  emissionSources: LL97EmissionSource[];
}

export const startLL97Filing = (buildingId: string) =>
  api.post("/audits/ll97/start_filing/", { building_id: buildingId });

export const startLL88Filing = (buildingId: string) =>
  api.post("/audits/ll88/start_filing/", { building_id: buildingId });

export const fetchLl97SubmissionDetail = (submissionId: string) =>
  api.get(`/audits/ll97/${submissionId}/`).then((res) => res.data);

export const fetchLl88SubmissionDetail = (submissionId: string) =>
  api.get(`/audits/ll88/${submissionId}/`).then((res) => res.data);

export const fetchLl97SubmissionOverview = (submissionId: string) =>
  api.get(`/audits/ll97/${submissionId}/overview/`).then((res) => res.data);

export const fetchLl88SubmissionOverview = (submissionId: string) =>
  api.get(`/audits/ll88/${submissionId}/overview/`).then((res) => res.data);

/**
 * Fetches LL97 compliance data from the server
 * This replaces the frontend implementation of getAllPeriodsData
 * by using the backend's calculation
 */
export const fetchLL97ComplianceData = (
  submissionId: string,
): Promise<LL97PeriodData[]> =>
  api
    .get(`/audits/ll97/${submissionId}/get_compliance_data/`)
    .then((res) => res.data);

/**
 * Fetches the audit trail activities for a LL97 submission
 */
export const fetchLL97AuditTrail = (submissionId: string) =>
  api.get(`/audits/ll97/${submissionId}/audit_trail/`).then((res) => res.data);

/**
 * Fetches the audit trail activities for a LL88 submission
 */
export const fetchLL88AuditTrail = (submissionId: string) =>
  api.get(`/audits/ll88/${submissionId}/audit_trail/`).then((res) => res.data);

/**
 * Fetches the signature requests for a LL97 submission
 */
export const fetchLL97SignatureRequests = (submissionId: string) =>
  api
    .get(`/audits/ll97/${submissionId}/signature_requests/`)
    .then((res) => res.data);

/**
 * Fetches the signature requests for a LL88 submission
 */
export const fetchLL88SignatureRequests = (submissionId: string) =>
  api
    .get(`/audits/ll88/${submissionId}/signature_requests/`)
    .then((res) => res.data);

export const updateLL97Submission = (submissionId: string, data: any) =>
  api.put(`/audits/ll97/${submissionId}/`, data);

export const updateLL88Submission = (submissionId: string, data: any) =>
  api.put(`/audits/ll88/${submissionId}/`, data);

export const submitLL97Dispute = (submissionId: string, data: any) =>
  api.post(`/audits/ll97/${submissionId}/submit_cbl_dispute/`, data);

export const fetchCompliancePathways = (params: any) =>
  api.post("/audits/get_compliance_pathways/", params).then((res) => res.data);

export const updateAttestationObj = (attestationId: string, data: any) =>
  api.patch(`/audits/attestation/${attestationId}/`, data);

export const openLL97ExtensionRequest = (submissionId: string) =>
  api.post(`/audits/ll97/${submissionId}/open_extension_request/`);

export const cancelLL97ExtensionRequest = (submissionId: string) =>
  api.post(`/audits/ll97/${submissionId}/cancel_extension_request/`);

export const submitLL97ExtensionRequest = (submissionId: string) =>
  api.post(`/audits/ll97/${submissionId}/submit_extension_request/`);

export const searchLL97Submissions = (params: any) =>
  api.get("/audits/ll97/", { params }).then((res) => res.data);

export interface LL97CBLDisputeItem {
  id: string;
  ll97Submissions: any[];
  cblDisputeStatus: number;
  nycBins: string[];
  status: number;
  message: string;
  createdAt: string;
  updatedAt: string;
}

export const fetchCBLDisputes = (): Promise<LL97CBLDisputeItem[]> =>
  api.get("/audits/cbl_disputes/").then((res) => res.data);

export const updateCBLDispute = (disputeId: string, fields: any) =>
  api.patch(`/audits/cbl_disputes/${disputeId}/`, fields);

// LL87 — generic LawSubmission endpoints
export const syncLl87Submission = (buildingId: string) =>
  api
    .post("/audits/law_submissions/sync/", { building_id: buildingId, law_code: "LL87" })
    .then((res) => res.data);

export const fetchLl87Submission = (submissionId: string) =>
  api.get(`/audits/law_submissions/${submissionId}/`).then((res) => res.data);

export const fetchLl87ActivityTrail = (submissionId: string) =>
  api.get(`/audits/law_submissions/${submissionId}/activity/`).then((res) => res.data);

export const advanceLl87Status = (submissionId: string, status: string, note?: string) =>
  api
    .post(`/audits/law_submissions/${submissionId}/advance/`, { status, note: note ?? "" })
    .then((res) => res.data);

export const attachLl87Vendor = (
  submissionId: string,
  args: {
    role: string;
    vendorId?: string;
    vendorNameRaw?: string;
    scheduledDate?: string;
  },
) =>
  api
    .post(`/audits/law_submissions/${submissionId}/vendors/`, {
      role: args.role,
      vendor_id: args.vendorId,
      vendor_name_raw: args.vendorNameRaw,
      scheduled_date: args.scheduledDate,
    })
    .then((res) => res.data);

// NEW-BE-3: `filing_confirmation` uploads carry optional DOE-tool
// metadata (Submission ID + submitted date, merged onto law_data
// server-side) and an optional attested_year (FR-19a — the server
// falls back to the submission's cycle_year). FormData bypasses the
// axios key transforms, so the snake_case field names post verbatim.
export const attachLl87File = (
  submissionId: string,
  args: {
    fileType: string;
    file: File;
    submissionIdString?: string;
    submittedDate?: string;
    attestedYear?: number;
  },
) => {
  const formData = new FormData();
  formData.append("file", args.file);
  formData.append("file_type", args.fileType);
  if (args.submissionIdString) {
    formData.append("submission_id", args.submissionIdString);
  }
  if (args.submittedDate) {
    formData.append("submitted_date", args.submittedDate);
  }
  if (args.attestedYear != null) {
    formData.append("attested_year", String(args.attestedYear));
  }
  return api
    .post(`/audits/law_submissions/${submissionId}/files/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
};

// FR-P3-7 (NEW-BE-5): owner-callable authorization-to-file intake. Posts the
// signer identity snake_case verbatim (`signer_name` / `signer_title`); the
// server mints `law_data.owner_authorization` (born `unverified`, server-
// authoritative `signed_at`) + one OWNER_AUTHORIZATION_RECORDED activity and
// returns the serialized submission. Idempotent: an identical signer re-post
// is a 200 no-op; a changed signer re-records (201). This is an AUTHORIZATION,
// never the RDP's §28-309 EER certification — the signer NEVER lands on
// `filing_attestation` and NEVER gates the resolver's `accepted` branch (H3).
export const recordLl87OwnerAuthorization = (
  submissionId: string,
  args: { signerName: string; signerTitle: string },
) =>
  api
    .post(`/audits/law_submissions/${submissionId}/request_owner_authorization/`, {
      signer_name: args.signerName,
      signer_title: args.signerTitle,
    })
    .then((res) => res.data);

// FR-P3-9 (NEW-BE-2): staff-only send of the recorded DOB objection package as
// an INTERNAL RDP-handoff email. Takes NO body — the service binds recipients
// to the hardcoded internal allowlist (leo@ / zeid@), NEVER the customer's real
// RDP (building-mode recipients policy). A 200 resolves `{ sent: true }` (the
// package was forwarded to CarbonClear for handoff); any error (403 non-staff /
// 400 no-objection / 502 email failure) rejects, and the caller renders the
// honest not-available note. The FE NEVER claims the customer's RDP received
// anything.
export const sendLl87ObjectionsToRdp = (
  submissionId: string,
): Promise<{ sent: boolean }> =>
  api
    .post(`/audits/law_submissions/${submissionId}/send_objections_to_rdp/`)
    .then(() => ({ sent: true }));

// NEW-BE-7: owner extension-request intake — an EXTENSION_REQUESTED
// activity record ONLY. Status and deadline NEVER change here (a staff
// grant later is the existing advance to EXTENSION_GRANTED); the
// request is idempotent server-side (identical repeat = 200 no-op).
// Returns the serialized submission.
export const requestLl87Extension = (
  submissionId: string,
  args: {
    track: "good_faith" | "hardship";
    groundClaimed?: string;
    notes?: string;
  },
) =>
  api
    .post(`/audits/law_submissions/${submissionId}/request_extension/`, {
      track: args.track,
      ground_claimed: args.groundClaimed,
      notes: args.notes,
    })
    .then((res) => res.data);

// FR-P2-1: owner/PM intro-request intake — persists ONLY owner-authored
// fields (`roles`, `notes`, owner-typed `preferred_timeframe`). The
// boundary NEVER posts vendor_name / date_label / time (those fabricated
// sample-pool values stay quarantined in the FE sessionStorage shim,
// §2 / FR-P2-2). Role VALUES and keys post snake_case verbatim.
export const requestLl87Intro = (
  submissionId: string,
  args: {
    roles: string[];
    notes?: string;
    preferredTimeframe?: string;
  },
) =>
  api
    .post(`/audits/law_submissions/${submissionId}/request_intro/`, {
      roles: args.roles,
      notes: args.notes,
      preferred_timeframe: args.preferredTimeframe,
    })
    .then((res) => res.data);

// FR-P2-8: owner deferral CLAIM intake — writes law_data.deferral with
// verified:false (an owner can never self-verify) + a review item. NO
// status change (never mints DEFERRAL_FILED). Ground token posts
// verbatim; `eer1_filed_date` is optional at claim time (staff supply it
// at verification). Returns the serialized submission.
export const requestLl87DeferralClaim = (
  submissionId: string,
  args: {
    ground: string;
    eer1FiledDate?: string;
    dobSubmissionId?: string;
  },
) =>
  api
    .post(`/audits/law_submissions/${submissionId}/request_deferral/`, {
      ground: args.ground,
      eer1_filed_date: args.eer1FiledDate,
      dob_submission_id: args.dobSubmissionId,
    })
    .then((res) => res.data);

// FR-C-7 — the tri-party correspondence thread (audit.LawSubmissionMessage).
// GET returns the reader's VISIBILITY-FILTERED thread (owners get
// `owner_visible` rows only — filtered SERVER-side; staff-internal notes never
// reach this payload). POST is TEXT-ONLY this phase: the body is `{ body }` and
// nothing else — a `files` key 400s by design (attachments deferred, §9), and
// party + visibility are FORCED server-side for non-staff so the FE never sends
// them. Flat snake_case fields + opaque token VALUES camelize safely (§3.7-1).
export const fetchLl87Messages = (
  submissionId: string,
): Promise<Ll87Message[]> =>
  api
    .get(`/audits/law_submissions/${submissionId}/messages/`)
    .then((res) => res.data);

// The OWNER path sends `{ body }` and nothing else (party + visibility are
// FORCED server-side for non-staff). The STAFF lane (FR-C-8) may additionally
// pass `party` ∈ {carbonclear,rdp,dob} + `visibility`, and on a `dob`-party
// owner-visible summary the `status_from`/`status_to` VALUES that build the DOB
// status card (there is no prose field).
//
// The status VALUES ride a NESTED `extraData` object: the recursive request
// decamelizer (`base.ts`) rewrites `extraData:{statusFrom,statusTo}` into
// `extra_data:{status_from,status_to}` — the EXACT nested shape the BE messages
// action reads (`request.data.get("extra_data")`). Top-level status keys would
// decamelize top-level and the BE silently drops them (the DOB card never
// stamps). `extraData` is sent ONLY when a status key is present — the owner
// path (and any staff post without status keys) is a byte-identical `{ body }`
// (sending an empty/owner `extraData` is a smell: the BE only whitelists the
// status keys on the staff DOB path).
export const postLl87Message = (
  submissionId: string,
  args: {
    body: string;
    party?: string;
    visibility?: string;
    statusFrom?: string;
    statusTo?: string;
  },
): Promise<Ll87Message> => {
  const hasStatus =
    args.statusFrom !== undefined || args.statusTo !== undefined;
  return api
    .post(`/audits/law_submissions/${submissionId}/messages/`, {
      body: args.body,
      ...(args.party !== undefined ? { party: args.party } : {}),
      ...(args.visibility !== undefined ? { visibility: args.visibility } : {}),
      ...(hasStatus
        ? {
            extraData: {
              ...(args.statusFrom !== undefined
                ? { statusFrom: args.statusFrom }
                : {}),
              ...(args.statusTo !== undefined
                ? { statusTo: args.statusTo }
                : {}),
            },
          }
        : {}),
    })
    .then((res) => res.data);
};

// FR-C-4: staff-only route of an owner-visible message toward the RDP as an
// INTERNAL handoff email. Body is `{ messageId }` (decamelizes to `message_id`);
// the BE binds recipients to the internal allowlist (leo@ / zeid@) and NAMES —
// never delivers to — the customer's real RDP (dark-launch). 200 returns the
// FULL submission serializer; 502 = transient SES failure with NOTHING recorded
// (safe to retry); 400 if the message is `staff_internal`; 404 if it isn't on
// this submission. The FE renders receipt truth from the REFETCHED
// `extra_data.routed_recipients`, never a client-side delivery claim.
export const routeLl87MessageToRdp = (
  submissionId: string,
  messageId: string,
) =>
  api
    .post(`/audits/law_submissions/${submissionId}/route_message_to_rdp/`, {
      messageId,
    })
    .then((res) => res.data);

// NEW-BE-6: the constrained, whitelisted law_data write path. Keys
// outside the caller's per-law whitelist 400 (staff-only keys 403 for
// owners); the merge is server-side and never replaces law_data
// wholesale. Patch keys are the BE's snake_case tokens, posted
// verbatim. Returns the serialized submission.
export const patchLl87LawData = (
  submissionId: string,
  patch: Record<string, unknown>,
) =>
  api
    .post(`/audits/law_submissions/${submissionId}/law_data/`, { patch })
    .then((res) => res.data);

// PR-D (LL87 FR-19b) — owner attestation over the bulk endpoint.
// One evidence record per building; the response reports a per-building
// outcome (never a batch 500). The single-building "I already filed —
// confirm it" CTA calls this with one id. Outcomes arrive camelized by
// the axios interceptor (`buildingId` / `submissionId`); outcome tokens
// are opaque VALUES and pass through verbatim.
export interface BulkAttestResult {
  buildingId: string;
  submissionId?: string;
  outcome: "attested" | "unchanged" | "not_found" | "sync_refused" | "error";
  /** Present on `sync_refused` — the FR-29 refusal token. */
  error?: string;
  /** Present on `sync_refused` / `error` — machine reason / detail. */
  reason?: string;
  detail?: string;
}

export interface BulkAttestResponse {
  lawCode: string;
  kind: "filed" | "not_filed";
  attestedYear: number | null;
  results: BulkAttestResult[];
}

export const bulkAttestLawSubmissions = (args: {
  lawCode: string;
  kind: "filed" | "not_filed";
  attestedYear?: number;
  buildingIds: string[];
}): Promise<BulkAttestResponse> =>
  api
    .post("/audits/law_submissions/bulk_attest/", {
      law_code: args.lawCode,
      kind: args.kind,
      attested_year: args.attestedYear,
      building_ids: args.buildingIds,
    })
    .then((res) => res.data);

// LL87 chat-intro FR-CI-9 — the planner's PRIMARY durable path: the
// FR-P4-11 `bulk_request_intro` fan-out (IsAuthenticated — it reaches
// the non-staff G9-graduated PM the chat gate 403s). Owner-authored
// fields ONLY (`roles` / `notes` / `preferred_timeframe`, mirroring
// `requestLl87Intro` above) — NEVER vendor_name / date / time. Each
// notes/timeframe rides INSIDE its items[] entry: the endpoint rejects
// a top-level shared blob with a 400. Outcomes arrive camelized
// (`buildingId`, tally `notFound`/`syncRefused`); outcome tokens are
// opaque VALUES and pass through verbatim.
export interface BulkRequestIntroItem {
  buildingId: string;
  /** Per-building roles; omitted → the batch `roles` default applies. */
  roles?: string[];
  notes?: string;
  preferredTimeframe?: string;
}

export interface BulkRequestIntroResult {
  buildingId: string;
  submissionId?: string;
  outcome: "requested" | "unchanged" | "not_found" | "sync_refused" | "error";
  /** Present on `sync_refused` — the FR-29 refusal token. */
  error?: string;
  /** Present on `sync_refused` / `error` — machine reason / detail. */
  reason?: string;
  detail?: string;
}

/** The writer's honest 5-key tally (keys camelized by the axios boundary). */
export interface BulkRequestIntroTally {
  requested: number;
  unchanged: number;
  notFound: number;
  syncRefused: number;
  error: number;
}

export interface BulkRequestIntroResponse {
  lawCode: string;
  results: BulkRequestIntroResult[];
  tally: BulkRequestIntroTally;
}

export const bulkRequestIntro = (args: {
  lawCode?: string;
  /** Batch default roles — pin to the full sorted LL87 role set. */
  roles: string[];
  items: BulkRequestIntroItem[];
}): Promise<BulkRequestIntroResponse> =>
  api
    .post("/audits/law_submissions/bulk_request_intro/", {
      law_code: args.lawCode ?? "LL87",
      roles: args.roles,
      items: args.items.map((item) => ({
        building_id: item.buildingId,
        ...(item.roles !== undefined ? { roles: item.roles } : {}),
        ...(item.notes !== undefined ? { notes: item.notes } : {}),
        ...(item.preferredTimeframe !== undefined
          ? { preferred_timeframe: item.preferredTimeframe }
          : {}),
      })),
    })
    .then((res) => res.data);
